import { Box, Typography, Button, Paper, Alert, Stack } from "@mui/material";
import { ErrorOutline, Refresh } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import React from "react";
import { logger } from "../utils/logger";

// Separate component to use hooks
const ErrorBoundaryContent = ({
  error,
  errorInfo,
  errorId,
  onRetry,
  onReload,
}) => {
  const { t } = useTranslation("common");
  const isDevelopment = import.meta.env.MODE === "development";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.default",
        p: 3,
      }}
    >
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: "100%",
          p: 4,
          textAlign: "center",
        }}
      >
        <Stack spacing={3} alignItems="center">
          <ErrorOutline
            sx={{
              fontSize: 64,
              color: "error.main",
            }}
          />

          <Typography variant="h4" component="h1" gutterBottom>
            {t("common.error_something_wrong")}
          </Typography>

          <Typography variant="body1" color="text.secondary">
            {t("common.error_inconvenience_message")}
          </Typography>

          {isDevelopment && (
            <Alert severity="error" sx={{ width: "100%", textAlign: "left" }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("common.error_details_dev_mode")}
              </Typography>
              <Typography
                variant="body2"
                component="pre"
                sx={{ fontSize: "0.75rem", mb: 1 }}
              >
                {error && error.toString()}
              </Typography>
              {errorInfo && (
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontSize: "0.75rem",
                    maxHeight: 200,
                    overflow: "auto",
                  }}
                >
                  {errorInfo.componentStack}
                </Typography>
              )}
            </Alert>
          )}

          {errorId && (
            <Typography variant="caption" color="text.secondary">
              {t("common.error_id")}: {errorId}
            </Typography>
          )}

          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={onRetry}
              startIcon={<Refresh />}
            >
              {t("common.try_again")}
            </Button>

            <Button variant="outlined" onClick={onReload}>
              {t("common.reload_page")}
            </Button>
          </Stack>

          <Typography variant="body2" color="text.secondary">
            {t("common.error_contact_support")}
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    logger.error("Error Boundary caught an error:", error);
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate a unique error ID for tracking
    const errorId = `error_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    // Log the error for debugging
    logger.error("Error Boundary caught an error:", error, errorInfo);

    // Update state with error details
    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // In production, you would send this to your error monitoring service
    if (import.meta.env.MODE === "production") {
      // Example: Send to error tracking service
      // Sentry.captureException(error, {
      //   tags: { errorBoundary: true },
      //   extra: { errorInfo, errorId }
      // });
    }
  }

  handleRetry = () => {
    // Reset the error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleReload = () => {
    // Reload the entire page as a last resort
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId } = this.state;

      return (
        <ErrorBoundaryContent
          error={error}
          errorInfo={errorInfo}
          errorId={errorId}
          onRetry={this.handleRetry}
          onReload={this.handleReload}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
